import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/da2kegbd3r93mn1k3ak0";

const topics = ["角色资料投稿", "信息纠错", "CP整理补充", "合作交流", "其他"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("loading");
    setFormError("");
    setSuccessMsg("");

    const form = e.currentTarget;
    const fd = new FormData(form);

    // 蜜罐检测：命中则不提交，并给出与正常提交相同的反馈
    const honeypot = (fd.get("company_alt") ?? "").toString().trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("感谢你的投稿！我们已经收到，会尽快审核整理。");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "company_alt") return;
      if (value && typeof value === "string" && value.trim()) {
        body.append(key, value);
      }
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setSuccessMsg(serverMsg || "感谢你的投稿！我们已经收到，会尽快审核整理。");
        form.reset();
        return;
      }

      // 失败：保留输入，展示服务器信息
      setStatus("error");
      setFormError(
        (serverMsg || "提交失败，请稍后重试。").length > 200
          ? `${(serverMsg || "提交失败").slice(0, 200)}…`
          : serverMsg || "提交失败，请稍后重试。"
      );
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <form data-readdy-form="da2kegbd3r93mn1k3ak0" onSubmit={handleSubmit} noValidate>
      {/* 蜜罐字段 */}
      <div className="hp-field" aria-hidden="true">
        <label htmlFor="company_alt">Company</label>
        <input type="text" name="company_alt" id="company_alt" tabIndex={-1} autoComplete="off" readOnly />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
            你的昵称
          </label>
          <input
            id="cf-name"
            name="昵称"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
          投稿类型
        </label>
        <select
          id="cf-topic"
          name="投稿类型"
          className="w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        >
          {topics.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-msg" className="mb-1.5 block text-sm font-medium text-foreground-800">
          内容说明
        </label>
        <textarea
          id="cf-msg"
          name="内容说明"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你想投稿的角色资料、纠错信息或CP整理内容，500字以内。"
          className="w-full resize-none rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        ></textarea>
        <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
      </div>

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "loading" ? (
          <>
            <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" aria-hidden="true"></i>
            提交投稿
          </>
        )}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line mt-0.5" aria-hidden="true"></i>
          {successMsg}
        </p>
      )}
      {status === "error" && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-xl bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
          {formError}
        </p>
      )}
    </form>
  );
}